import React from "react";
import { CognitoUserPool, CognitoRefreshToken } from "amazon-cognito-identity-js";
import { defaultMetricColor, userRoles } from "../constant";
import { LOCAL_TENANT, TEST_ENVS_OPT_IN } from "../env";

export const setToken = (token: string) => {
  localStorage.setItem("token", token);
};

export const setRefreshToken = (refreshToken: string) => {
  // set to local storage
  localStorage.setItem("refreshToken", refreshToken);
};

export const setUser = (user: any) => {
  // set to local storage
  localStorage.setItem("user", JSON.stringify(user));
};

export const getUser = () => {
  // get from local storage
  const user = localStorage.getItem("user");
  return user ? JSON.parse(user) : null;
};

export const getConsent = () => {
  // get from local storage
  const consent = localStorage.getItem("consent");
  return consent ? JSON.parse(consent) : null;
};

export const setConsent = (consent: any) => {
  // set to local storage
  localStorage.setItem("consent", JSON.stringify(consent));
};

export function checkPasswordStrength(password) {
  const requirements = [
    { regex: /[a-z]/, message: "lowercase letter" },
    { regex: /[A-Z]/, message: "uppercase letter" },
    { regex: /\d/, message: "digit" },
    { regex: /[@$!%*?&]/, message: "special character" },
    { regex: /.{8,}/, message: "at least 8 characters" },
  ];
  let score = 0;
  let message = "";
  requirements.forEach((requirement) => {
    if (requirement.regex.test(password)) {
      score++;
    } else {
      message += ` ${requirement.message},`;
    }
  });
  if (score === requirements.length) {
    message = "Your password is great. Nice work!";
  } else {
    message = `Your password needs ${message.slice(0, -1)}`;
  }
  return { score: (score / requirements.length) * 100, message };
}

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s+@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const clearTokenAndUser = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
};

export function formatNumber(num: number) {
  const absNum = Math.abs(num);

  if (absNum >= 1e9) {
    return (num / 1e9).toFixed(1).replace(/\.0$/, "") + "B";
  } else if (absNum >= 1e6) {
    return (num / 1e6).toFixed(1).replace(/\.0$/, "") + "M";
  } else if (absNum >= 1e3) {
    return (num / 1e3).toFixed(1).replace(/\.0$/, "") + "k";
  } else {
    return num.toString();
  }
}

export function getInitial(firstName: string, lastName: string) {
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`;
  } else if (firstName) {
    return firstName[0];
  } else if (lastName) {
    return lastName[0];
  } else {
    return "";
  }
}

export function buildAssetUrl(tenant: string, stage: string) {
  // s3://{stage}.{tenant}.assets.praxis.com
  let url = "";
  if (stage) {
    url = `${stage}.`;
  }
  return `${url}${tenant}.assets.praxis.com`;
}

const FEATURE_DEPLOY_MARKER = "feature-";
export const FEATURE_HEADER = "X-Feature";

/**
 * Dev-only "feature deploys" are served from a host whose first label is
 * `feature-<id>` (e.g. `feature-new-models.test.praxispro.ai`). Returns `<id>`
 * for such hosts and `null` for every regular tenant host.
 *
 * Gated on `TEST_ENVS_OPT_IN` so the mechanism is inert wherever the env var
 * isn't set (prod), regardless of what the hostname looks like.
 */
export function getFeatureDeployId(hostname: string = window.location.hostname): string | null {
  if (!TEST_ENVS_OPT_IN) {
    return null;
  }
  const firstLabel = hostname.replace("www.", "").split(".")[0];
  if (!firstLabel.startsWith(FEATURE_DEPLOY_MARKER)) {
    return null;
  }
  return firstLabel.slice(FEATURE_DEPLOY_MARKER.length) || null;
}

/**
 * Drops the `feature-<id>.` label so tenant resolution sees the underlying
 * tenant host (`feature-new-models.test.praxispro.ai` → `test.praxispro.ai`).
 */
export function stripFeatureDeployLabel(hostname: string): string {
  if (!getFeatureDeployId(hostname)) {
    return hostname;
  }
  const withoutWww = hostname.replace("www.", "");
  return withoutWww.slice(withoutWww.indexOf(".") + 1);
}

/**
 * Headers identifying the calling feature deploy. The sessions service routes
 * `POST /sessions/sessions` on `X-Feature` (praxis-pro-sessions-service#196);
 * no header (or an unknown value) means the stable behaviour.
 */
export function getFeatureDeployHeaders(): Record<string, string> {
  const featureId = getFeatureDeployId();
  return featureId ? { [FEATURE_HEADER]: featureId } : {};
}

export function getTenantKey() {
  const url = window.location.href;
  if (url.includes("localhost") || url.includes("dev.app.praxispro.ai")) {
    return `${LOCAL_TENANT}.praxispro.ai`;
  }

  const host = url.split("/")[2].replace("www.", "");
  return stripFeatureDeployLabel(host).replace("-dev", "");
}

export function getTenantId() {
  return getTenantKey().split(".praxispro.ai")[0].replace("-dev", "");
}

/**
 * Tenant label for the per-tenant API host: the last label before
 * `praxispro.ai`, so `ingrezza.neurocrine-dev.praxispro.ai` gives
 * `neurocrine-dev`. The product label ("ingrezza") is dropped and, unlike
 * `getTenantId`, the `-dev` suffix is kept — the API host and the asset paths
 * genuinely disagree on this.
 */
export function getApiTenant(hostname: string = window.location.hostname): string {
  if (hostname.includes("localhost") || hostname.includes("dev.app.praxispro.ai")) {
    return LOCAL_TENANT;
  }
  const labels = stripFeatureDeployLabel(hostname.replace("www.", ""))
    .split(".praxispro.ai")[0]
    .split(".");
  return labels[labels.length - 1];
}

/**
 * Base URL of the per-tenant API host. The auth endpoints have to be reached
 * here rather than through the shared gateway host: the refresh cookie is
 * scoped to `.<tenant>.praxispro.ai`, and a browser drops a Set-Cookie whose
 * Domain doesn't match the host that sent it.
 */
export function getApiBaseUrl(hostname?: string): string {
  return `https://api.${getApiTenant(hostname)}.praxispro.ai`;
}

export function getDatadogEnv(): string {
  const url = window.location.href;
  if (url.includes("localhost")) {
    return "local";
  }
  const hostname = stripFeatureDeployLabel(url.split("/")[2].replace("www.", ""));
  return hostname.split(".praxispro.ai")[0];
}

export function getPostHogKey(key: string) {
  // key: sso_login
  // url: ingrezza.neurocrine.praxispro.ai
  // url2: ingrezza.neurocrine-dev.praxispro.ai

  // output1: ingrezza_neurocrine_sso_login
  // output2: ingrezza_neurocrine-dev_sso_login

  let url = window.location.origin;
  if (url.includes("localhost") || url.includes("dev.app.praxispro.ai")) {
    url = `http://${LOCAL_TENANT}.praxispro.ai`;
  }
  // split url with //
  url = url.split("//")?.[1] || "";
  // drop the feature-<id> label (if any) so feature deploys key the same as their underlying tenant
  url = stripFeatureDeployLabel(url);
  // trim ".praxispro.ai" and strip www prefix
  url = url.replace("www.", "").replace(".praxispro.ai", "");
  // now replace all . with _
  url = url.replaceAll(".", "_");
  // join url with key using _ (underscore)
  url = `${url || ""}_${key}`;
  return url;
}


/**
 * Convert a color to rgba format, handling CSS variables
 * @param {string|Array} color - Color in hex, rgb, css var format, or as [r,g,b] array
 * @param {number} alpha - Alpha value between 0 and 1
 * @returns {string} - rgba string or css variable with opacity
 */
export const getRgbaColor = (color, alpha = 0.27) => {
  // Handle CSS variables
  if (typeof color === 'string' && color.startsWith('var(')) {
    // For CSS variables, we need to use the rgba() function with the variable directly
    // or use the alpha value with RGB through the / syntax (for modern browsers)
    return `rgb(var(${color.substring(4, color.length - 1)}) / ${alpha})`;
  }

  // Initialize RGB values
  let r, g, b;

  // Handle hex code
  if (typeof color === 'string' && color.startsWith('#')) {
    r = parseInt(color.slice(1, 3), 16);
    g = parseInt(color.slice(3, 5), 16);
    b = parseInt(color.slice(5, 7), 16);
  }
  // Handle rgb string
  else if (typeof color === 'string' && color.startsWith('rgb')) {
    const match = color.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
    if (match) {
      r = parseInt(match[1], 10);
      g = parseInt(match[2], 10);
      b = parseInt(match[3], 10);
    }
  }
  // Handle array of RGB values
  else if (Array.isArray(color) && color.length >= 3) {
    [r, g, b] = color;
  }

  // Return rgba format if we have valid RGB values
  if (r !== undefined && g !== undefined && b !== undefined) {
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  // Return the original color if we couldn't parse it
  return typeof color === 'string' ? color : 'rgba(0, 0, 0, 0.27)';
};

export const secondsToHours = (seconds: number) => {
  let hours: number | string = Math.floor(seconds / 3600);
  // if (hours < 10) {
  //   hours = `0${hours}`;
  // }
  let minutes: number | string = Math.floor((seconds % 3600) / 60) + (hours * 60);
  if (minutes < 10) {
    minutes = `0${minutes}`;
  }
  let secondsLeft: number | string = seconds % 60;
  if (secondsLeft < 10) {
    secondsLeft = `0${secondsLeft}`;
  }
  return `${minutes}:${secondsLeft}`;
}

export function getPractitionerTypeMap(tenantProfile: any): Record<string, string> {
  const flatCohorts = Object.values(
    tenantProfile?.practitioner_types?.cohorts || {}
  ).flat() as any[];
  const defaultTypes = tenantProfile?.practitioner_types?.default || [];
  return [...flatCohorts, ...defaultTypes].reduce((acc: Record<string, string>, curr: any) => {
    acc[curr.key] = curr.type;
    return acc;
  }, {});
}

export const getPractitionerTypes = (practitionerTypes: any, group?: string) => {
  const defaultTypes = practitionerTypes?.default || [];
  if (group) {
    const key = Object.keys(practitionerTypes?.cohorts || {}).find((key) => key === group);
    if (key) {
      return practitionerTypes.cohorts?.[key] || defaultTypes;
    }
    return defaultTypes;
  }
  return defaultTypes;
};

/**
 * Effective `hcpTag` to use when filtering customizer personalities and
 * practitioner_types.
 *
 * Per `CLAUDE.md` (HCP Tags & Personality Scoping) / ADR-006 / RFC-UT-PHASE2-001,
 * tag scoping is only meaningful when the group itself declares `hcpTags`. In
 * that case the parent always supplies a non-empty `hcpTag` and we forward it
 * unchanged.
 *
 * When the group does NOT declare `hcpTags`, the parent passes an empty string
 * and scoping is explicitly unsupported — every personality is valid for every
 * practitioner type, even if cohort `practitioner_types` carry stray tags
 * (treated as misconfiguration and ignored). We return `undefined` so the
 * customizer applies no tag filter to either pool.
 *
 * Without this contract, the customizer would silently scope to the first
 * cohort tag, hiding the user's already-selected practitioner and letting them
 * submit a personality from a different indication.
 */
export function resolveCustomizerHcpTag(hcpTag: string | undefined): string | undefined {
  return hcpTag || undefined;
}

export type PersonalityItem = {
  key: string;
  label: string;
  tags?: string[];
};

const normalizePersonalityItems = (items: any[]): PersonalityItem[] => {
  return items.map((item) => {
    if (typeof item === "string") {
      return { key: item, label: item };
    }
    return {
      key: item.key,
      label: item.label || item.key,
      tags: item.tags,
    };
  });
};

const isCohortPersonalitiesFormat = (personalities: any) => {
  return !!(personalities?.default || personalities?.cohorts);
};

export const filterPersonalitiesByTag = (
  personalities: PersonalityItem[],
  tag?: string,
): PersonalityItem[] => {
  if (!tag) {
    return personalities.filter((personality) => !personality.tags?.length);
  }
  return personalities.filter(
    (personality) => !personality.tags?.length || personality.tags.includes(tag),
  );
};

/** Tag-filtered personalities; falls back to the full cohort when the filter matches none. */
export const resolvePersonalitiesForPractitionerTag = (
  personalities: PersonalityItem[],
  tag?: string,
): PersonalityItem[] => {
  const filtered = filterPersonalitiesByTag(personalities, tag);
  return filtered.length > 0 ? filtered : personalities;
};

export const getPersonalities = (
  personalities: any,
  group?: string,
  practitionerTag?: string,
): PersonalityItem[] => {
  if (!personalities) {
    return [];
  }

  let items: PersonalityItem[];

  if (Array.isArray(personalities)) {
    items = normalizePersonalityItems(personalities);
  } else if (isCohortPersonalitiesFormat(personalities)) {
    const defaultPersonalities = personalities.default || [];
    if (group) {
      const cohortKey = Object.keys(personalities.cohorts || {}).find((key) => key === group);
      if (cohortKey) {
        // An empty cohort array is treated the same as a missing cohort —
        // fall back to `default`. The previous `||` form also fell through
        // for null/undefined, but not for `[]` since arrays are truthy;
        // explicit length check handles both consistently.
        const cohortPersonalities = personalities.cohorts?.[cohortKey];
        items = normalizePersonalityItems(
          cohortPersonalities?.length ? cohortPersonalities : defaultPersonalities,
        );
      } else {
        items = normalizePersonalityItems(defaultPersonalities);
      }
    } else {
      items = normalizePersonalityItems(defaultPersonalities);
    }
  } else {
    items = Object.entries(personalities).map(([key, label]) => ({
      key,
      label: (label as string) || key,
    }));
  }

  if (practitionerTag) {
    return resolvePersonalitiesForPractitionerTag(items, practitionerTag);
  }
  return items;
};

export const getPersonalitiesMap = (
  personalities: any,
  group?: string,
  practitionerTag?: string,
): Record<string, string> => {
  return getPersonalities(personalities, group, practitionerTag).reduce((acc: Record<string, string>, { key, label }) => {
    acc[key] = label;
    return acc;
  }, {});
};

export const getAllPersonalitiesMap = (personalities: any): Record<string, string> => {
  if (!personalities) {
    return {};
  }

  if (Array.isArray(personalities)) {
    return getPersonalitiesMap(personalities);
  }

  if (isCohortPersonalitiesFormat(personalities)) {
    const allItems = [
      ...(personalities.default || []),
      ...Object.values(personalities.cohorts || {}).flat(),
    ] as any[];
    return getPersonalitiesMap(allItems);
  }

  return getPersonalitiesMap(personalities);
};

export type CallFocusTopicItem = {
  key: string;
  label: string;
  description?: string;
  tags?: string[];
};

/**
 * Normalize raw Call Focus topic entries to `CallFocusTopicItem`. Tolerates
 * the legacy flat-array shape where the identifier was `value` instead of
 * `key`, drops entries without a usable identifier, and defaults `label`
 * to the identifier when missing.
 */
const normalizeCallFocusItems = (items: any[] | undefined): CallFocusTopicItem[] => {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => {
      const key = String(item?.key ?? item?.value ?? "");
      if (!key) return null;
      return {
        key,
        label: String(item?.label ?? key),
        description: item?.description,
        tags: item?.tags,
      } as CallFocusTopicItem;
    })
    .filter((t): t is CallFocusTopicItem => t !== null);
};

/**
 * Tag filter for Call Focus topics — mirrors `filterPersonalitiesByTag`.
 * Untagged topics are treated as universal and included under any tag (and
 * are the only ones returned when no tag is supplied).
 */
export const filterCallFocusTopicsByTag = (
  topics: CallFocusTopicItem[],
  tag?: string,
): CallFocusTopicItem[] => {
  if (!tag) {
    return topics.filter((t) => !t.tags?.length);
  }
  return topics.filter((t) => !t.tags?.length || t.tags.includes(tag));
};

/** Tag-filtered topics; falls back to the full cohort when the filter matches none. */
export const resolveCallFocusTopicsForPractitionerTag = (
  topics: CallFocusTopicItem[],
  tag?: string,
): CallFocusTopicItem[] => {
  const filtered = filterCallFocusTopicsByTag(topics, tag);
  return filtered.length > 0 ? filtered : topics;
};

/**
 * Resolve Call Focus topics for a given group + practitioner tag.
 *
 * Mirrors `getPersonalities`:
 *  - Accepts both the legacy flat-array shape and the new
 *    `{ default, cohorts }` shape. Legacy arrays are ungrouped and ignore
 *    `group` — they're treated as the default list.
 *  - When `group` matches a cohort, returns that cohort's list; otherwise
 *    falls back to `default`.
 *  - When `practitionerTag` is supplied, filters via
 *    `resolveCallFocusTopicsForPractitionerTag` (untagged stay universal,
 *    full cohort fallback when nothing matches).
 */
export const getCallFocusTopics = (
  topics: any,
  group?: string,
  practitionerTag?: string,
): CallFocusTopicItem[] => {
  if (!topics) return [];

  let rawItems: any[] | undefined;
  if (Array.isArray(topics)) {
    rawItems = topics;
  } else {
    // An empty cohort array is treated the same as a missing cohort — fall
    // back to `default`. A configured-but-empty cohort is almost always a
    // misconfiguration, and silently rendering "no topics" is worse than
    // showing the tenant-wide defaults.
    const cohortTopics = group ? topics.cohorts?.[group] : undefined;
    rawItems = cohortTopics?.length ? cohortTopics : (topics.default || []);
  }

  const items = normalizeCallFocusItems(rawItems);

  if (practitionerTag) {
    return resolveCallFocusTopicsForPractitionerTag(items, practitionerTag);
  }
  return items;
};

/**
 * Drops topic IDs that aren't valid for the current group + tag scope.
 * Mirrors the customizer picker (`getCallFocusTopics` + `resolveCustomizerHcpTag`).
 * Apply on every `startCall` path (manual Begin and assignment auto-start).
 */
export function filterTopicsForGroupAndTag(
  topics: unknown,
  tenantProfile: any,
  group: string | undefined,
  hcpTag: string,
): unknown {
  if (!Array.isArray(topics) || topics.length === 0) return topics;
  const allowedTopicKeys = new Set(
    getCallFocusTopics(
      tenantProfile?.topics,
      group,
      resolveCustomizerHcpTag(hcpTag),
    ).map((t) => t.key),
  );
  return topics.filter((t: any) => typeof t === "string" && allowedTopicKeys.has(t));
}

/**
 * Group/tag-scoped `{ key: label }` map for rendering saved topic IDs.
 * Mirrors `getCallFocusTopics` so labels match the customizer pool for the
 * same cohort and practitioner tag. First label wins if a key repeats in
 * the resolved list (defensive against misconfiguration).
 */
export function getCallFocusTopicsLabelMap(
  topics: any,
  group?: string,
  practitionerTag?: string,
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const { key, label } of getCallFocusTopics(topics, group, practitionerTag)) {
    if (!(key in map)) {
      map[key] = label;
    }
  }
  return map;
}

export async function refreshSSOToken(cognitoDomain: string, clientId: string, refreshToken: string): Promise<string> {
  const response = await fetch(`${cognitoDomain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "refresh_token",
      client_id: clientId,
      refresh_token: refreshToken,
    }),
  });
  const data = await response.json();
  if (data.id_token) {
    setToken(data.id_token);
    localStorage.setItem("id_token", data.id_token);
    if (data.access_token) {
      localStorage.setItem("access_token", data.access_token);
    }
    return data.id_token;
  }
  throw new Error("SSO token refresh failed: no id_token in response");
}

export function refreshCognitoToken(
  refreshTokenStr: string,
  poolData: { UserPoolId: string; ClientId: string }
): Promise<string> {
  return new Promise((resolve, reject) => {
    const userPool = new CognitoUserPool(poolData);
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      return reject(new Error("No Cognito user found"));
    }

    const RefreshToken = new CognitoRefreshToken({ RefreshToken: refreshTokenStr });

    cognitoUser.refreshSession(RefreshToken, (err, session) => {
      if (err) {
        return reject(err);
      }
      if (session) {
        const newToken = session?.idToken?.jwtToken;
        setToken(newToken);
        setRefreshToken(session?.refreshToken?.token);
        resolve(newToken);
      } else {
        reject(new Error("No session returned from Cognito refresh"));
      }
    });
  });
}

export const getDeviceType = () => {
  // desktop, mobile, tablet
  // check if it's a mobile device
  if (window.innerWidth < 768) {
    return "mobile";
  } else if (window.innerWidth < 1024) {
    return "tablet";
  } else {
    return "desktop";
  }
}

/**
 *
 * @param percentage
 * @param metric_ranges
 * [
            {
                "backgroundColor": "#FFEBEB",
                "max": 49,
                "min": 0,
                "strokeColor": "#FA7D7D"
            },
            {
                "backgroundColor": "#FFF9EB",
                "max": 79,
                "min": 50,
                "strokeColor": "#EAB962"
            },
            {
                "backgroundColor": "#E1EDE2",
                "max": 100,
                "min": 80,
                "strokeColor": "#14AE5C"
            }
    ],
 * @returns
 */

export const getPerformanceColor = (percentage: number, metric_ranges: any[]) => {
  const range = metric_ranges?.find((range: any) => percentage >= range.min && percentage <= range.max);
  if (!range) {
    return defaultMetricColor.low;
  }
  return {
    progressBg: range.backgroundColor,
    progress: range.strokeColor,
    text: range.strokeColor,
  };
}

export function getNumberSuffix(number: number): string {
  // Get the last digit and last two digits
  const lastDigit = number % 10;
  const lastTwoDigits = number % 100;

  // Special case for 11, 12, 13
  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return "th";
  }

  // For all other numbers, check the last digit
  switch (lastDigit) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

export const getObjectionTitle = (objectionLevel: string, objections: any[]) => {
  const objection = objections.find((objection) => objection.value === objectionLevel);
  return objection?.label || objectionLevel;
}

export const getPersonalityArray = (personality: string | string[]) => {
  if (Array.isArray(personality)) {
    return personality;
  }
  return [personality];
}

export function getPersonalityLabels(
  personalityType: string | string[],
  tenantProfile: any,
  group?: string,
  hcpTag?: string,
): string {
  const fullMap = group
    ? getPersonalitiesMap(tenantProfile?.personalities, group)
    : getAllPersonalitiesMap(tenantProfile?.personalities);
  const scopedMap = hcpTag
    ? getPersonalitiesMap(tenantProfile?.personalities, group, hcpTag)
    : fullMap;
  return getPersonalityArray(personalityType)
    .map((p) => scopedMap[p] || fullMap[p] || p)
    .filter(Boolean)
    .join(", ");
}

/**
 * Parses simple markdown (bold **text**) and newlines to JSX
 * Used for info badge tooltips that come from profile config
 */
export function parseInfoBadgeText(text: string): React.ReactNode {
  if (!text) return null;

  // Split by newlines first
  const lines = text.split('\n');

  return lines.map((line, lineIndex) => {
    if (!line.trim()) {
      // Empty line = paragraph break
      // return React.createElement('br', { key: `br-${lineIndex}` });
      // remove empty lines
      return;
    }

    // Parse **bold** text within each line
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    const parsedLine = parts.map((part, partIndex) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        // Bold text
        return React.createElement('strong', { key: `bold-${lineIndex}-${partIndex}` }, part.slice(2, -2));
      }
      return part;
    });

    return React.createElement(
      'span',
      { key: `line-${lineIndex}` },
      ...parsedLine,
      lineIndex < lines.length - 1 ? React.createElement('br', { key: `lbr-${lineIndex}` }) : null
    );
  });
}

export const isManager = (role?: string) => {
  return role === userRoles.Manager;
}

export const isTrainer = (role?: string) => {
  return role === userRoles.Trainer;
}

export const isAdmin = (role?: string) => {
  return role === userRoles.Admin;
}

export const isAccountSpecialist = (role?: string) => {
  // Sales Rep is the old role, Account Specialist is the new role
  return role === userRoles.AccountSpecialist || role === userRoles.SalesRep;
}

export const isMobileOS = (): boolean => {
  const ua = navigator.userAgent;
  if (/Android|iPad|iPhone|iPod/i.test(ua)) {
    return true;
  }
  // iPadOS 13+ reports as desktop Safari — detect via touch support
  if (
    navigator.maxTouchPoints > 0 &&
    ((navigator as any).userAgentData?.platform === "macOS" ||
      navigator.platform === "MacIntel")
  ) {
    return true;
  }
  return false;
}

export const isSupportedBrowser = (): boolean => {
  if ((navigator as any).userAgentData?.brands) {
    return (navigator as any).userAgentData.brands.some(
      (b: { brand: string }) => b.brand === "Google Chrome" || b.brand === "Microsoft Edge"
    );
  }
  const ua = navigator.userAgent;
  // Edge's UA also contains "Chrome", so allowing it just means dropping the
  // Edg/ exclusion below. OPR/ (Opera) stays excluded.
  return /Chrome/.test(ua) && !/OPR\//.test(ua);
};